import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUsers, 
  FiSearch, 
  FiMessageCircle, 
  FiMapPin, 
  FiStar, 
  FiFilter, 
  FiX, 
  FiCheck, 
  FiClock 
} = FiIcons;

const UserDirectory = () => {
  const navigate = useNavigate();
  const { addNotification } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock locations for filter
  const locations = ['All Locations', 'Sierra Leone', 'The Gambia', 'Online'];

  useEffect(() => {
    // Simulate loading users from an API
    const fetchUsers = async () => {
      setLoading(true);
      // In a real app, this would be an API call
      setTimeout(() => {
        const mockUsers = [
          {
            id: 1,
            name: 'Aminata Kargbo',
            avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
            location: 'Freetown, Sierra Leone',
            level: 'Advanced',
            online: true,
            lastActive: new Date(),
            points: 3450,
            bio: 'Mathematics tutor and community leader'
          },
          {
            id: 2,
            name: 'Omar Jallow',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
            location: 'Banjul, The Gambia',
            level: 'Intermediate',
            online: true,
            lastActive: new Date(),
            points: 2780,
            bio: 'Science enthusiast and study group organizer'
          },
          {
            id: 3,
            name: 'Fatou Conteh',
            avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
            location: 'Bo, Sierra Leone',
            level: 'Beginner',
            online: false,
            lastActive: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
            points: 1560,
            bio: 'English literature student looking for study partners'
          },
          {
            id: 4,
            name: 'Isatu Bangura',
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
            location: 'Online',
            level: 'Advanced',
            online: false,
            lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            points: 4120,
            bio: 'Leadership mentor and community volunteer'
          },
          {
            id: 5,
            name: 'Lamin Jobe',
            avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
            location: 'Serrekunda, The Gambia',
            level: 'Intermediate',
            online: true,
            lastActive: new Date(),
            points: 2350,
            bio: 'History student and quiz champion'
          },
          {
            id: 6,
            name: 'Mariama Sesay',
            avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
            location: 'Freetown, Sierra Leone',
            level: 'Advanced',
            online: false,
            lastActive: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
            points: 3890,
            bio: 'Geography teacher and educational content creator'
          },
          {
            id: 7,
            name: 'Ibrahim Sanneh',
            avatar: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=100&h=100&fit=crop&crop=face',
            location: 'Banjul, The Gambia',
            level: 'Beginner',
            online: true,
            lastActive: new Date(),
            points: 980,
            bio: 'New member excited to learn and connect'
          },
          {
            id: 8,
            name: 'Adama Koroma',
            avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=100&h=100&fit=crop&crop=face',
            location: 'Makeni, Sierra Leone',
            level: 'Intermediate',
            online: false,
            lastActive: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
            points: 2150,
            bio: 'Community organizer and mathematics enthusiast'
          },
          {
            id: 9,
            name: 'Mohammed Bah',
            avatar: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=100&h=100&fit=crop&crop=face',
            location: 'Kenema, Sierra Leone',
            level: 'Advanced',
            online: true,
            lastActive: new Date(),
            points: 3560,
            bio: 'Science teacher and educational mentor'
          },
          {
            id: 10,
            name: 'Isatou Ceesay',
            avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face',
            location: 'Online',
            level: 'Intermediate',
            online: false,
            lastActive: new Date(Date.now() - 45 * 60 * 1000), // 45 minutes ago
            points: 2780,
            bio: 'Remote learner passionate about leadership development'
          }
        ];
        setUsers(mockUsers);
        setLoading(false);
      }, 800); // Simulate network delay
    };

    fetchUsers();
  }, []);

  // Filter users based on search term and selected location
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.bio.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = 
      selectedLocation === 'All Locations' || 
      selectedLocation === '' || 
      user.location.includes(selectedLocation);
    
    return matchesSearch && matchesLocation;
  });

  // Handle initiating a private chat with a user
  const handleStartChat = (user) => {
    addNotification({
      type: 'info',
      title: 'Starting Chat',
      message: `Opening private chat with ${user.name}...`
    });

    // In a real app, this would create a chat room or navigate to an existing one
    setTimeout(() => {
      navigate('/chat', { state: { chatPartner: user }});
    }, 500);
  };

  // Format last active time
  const formatLastActive = (date) => {
    if (!date) return 'Unknown';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="space-y-6 pb-16 md:pb-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiUsers} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">App Users</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Connect with fellow learners, start private conversations, and build your network in the ILM Global community.
          </p>
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or bio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Filter Button (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <SafeIcon icon={FiFilter} className="w-5 h-5" />
            <span>Filter</span>
          </button>

          {/* Location Filter (Desktop) */}
          <div className="hidden md:block">
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            >
              <option value="">Location (All)</option>
              {locations.map((location, index) => (
                <option key={index} value={location === 'All Locations' ? '' : location}>
                  {location}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile Filters (Expandable) */}
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4 pt-4 border-t border-gray-200 md:hidden"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                >
                  <option value="">All Locations</option>
                  {locations.filter(l => l !== 'All Locations').map((location, index) => (
                    <option key={index} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          // Loading skeletons
          [...Array(6)].map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse"></div>
              </div>
              <div className="mt-4 h-8 bg-gray-200 rounded animate-pulse"></div>
            </motion.div>
          ))
        ) : filteredUsers.length > 0 ? (
          // Users list
          filteredUsers.map((user, index) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                    <div
                      className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${
                        user.online ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    ></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{user.name}</h3>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      <SafeIcon icon={user.online ? FiCheck : FiClock} className="w-3 h-3" />
                      <span>{user.online ? 'Online now' : `Last active: ${formatLastActive(user.lastActive)}`}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 bg-primary-50 px-2 py-1 rounded text-xs font-medium text-primary-600">
                  <SafeIcon icon={FiStar} className="w-3 h-3" />
                  <span>{user.points}</span>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center text-xs text-gray-500 mb-2">
                  <SafeIcon icon={FiMapPin} className="w-3 h-3 mr-1" />
                  <span>{user.location}</span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">{user.bio}</p>
              </div>

              <button
                onClick={() => handleStartChat(user)}
                className="w-full flex items-center justify-center space-x-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                <span>Message</span>
              </button>
            </motion.div>
          ))
        ) : (
          // No results
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 col-span-full text-center"
          >
            <SafeIcon icon={FiSearch} className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No users found</h3>
            <p className="text-gray-600 mb-4">
              We couldn't find any users matching your search criteria.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedLocation('');
                setShowFilters(false);
              }}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Clear filters
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default UserDirectory;